import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Modal from '../../../../components/Modals/Modal';
import { Container, Form, Row } from '../../../../components/commons';
import Input from '../../../../components/Input';
import Button from '../../../../components/Button';
import { useSyncApiIntegration } from '../../../../services/apiIntegrations.service';
import useToast from '../../../../hooks/useToast';

export interface SyncModalHandle {
  openSyncModal: (integrationId: string) => void;
}

type ModalRef = React.ElementRef<typeof Modal>;

const schema = yup.object().shape({
  startDate: yup.string().required('Data Inicial obrigatória').matches(/^\d{6}$/, 'Formato AAAAMM esperado (ex: 202501)'),
  endDate: yup.string().required('Data Final obrigatória').matches(/^\d{6}$/, 'Formato AAAAMM esperado (ex: 202512)'),
});

const SyncModal: React.ForwardRefRenderFunction<SyncModalHandle, {}> = (props, ref) => {
  const modalRef = useRef<ModalRef>(null);
  const toast = useToast();
  const [currentId, setCurrentId] = useState<string | null>(null);

  const syncMutation = useSyncApiIntegration();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      startDate: '',
      endDate: '',
    },
  });

  React.useImperativeHandle(ref, () => ({
    openSyncModal: (id: string) => {
      setCurrentId(id);
      reset({ startDate: '', endDate: '' });
      modalRef.current?.openModal();
    },
  }));

  const onSubmit = async (data: { startDate: string; endDate: string }) => {
    if (!currentId) return;

    try {
      await syncMutation.mutateAsync({
        id: currentId,
        startDate: data.startDate,
        endDate: data.endDate,
      });

      toast.successToast('Sincronização iniciada com sucesso!');
      modalRef.current?.closeModal();
    } catch (error) {
      toast.errorToast('Erro ao iniciar sincronização.');
    }
  };

  return (
    <Modal ref={modalRef} title='Sincronizar Integração'>
      <Container>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <p style={{ fontSize: 14, color: '#666', marginBottom: 15 }}>
            Informe o período para sincronização (Formato YYYYMM).
          </p>
          
          <Row justify="space-between" gap={10}>
            <div style={{ flex: 1 }}>
              <Input
                type='text'
                control={control}
                name="startDate"
                label="Período Inicial"
                placeholder="Ex: 202501"
                error={errors.startDate}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Input
                type='text'
                control={control}
                name="endDate"
                label="Período Final"
                placeholder="Ex: 202512"
                error={errors.endDate}
              />
            </div>
          </Row>

          <div style={{ marginTop: 20 }}>
            <Button
              label='Iniciar Sincronização'
              type='submit'
              color='primary'
              variantType='block'
              loading={syncMutation.isLoading}
            />
          </div>
        </Form>
      </Container>
    </Modal>
  );
};

export default React.forwardRef(SyncModal);
